import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbarMenu,
TableToolbarAction,
  TableSelectRow,
  TableBatchActions,
  TableBatchAction,
  Button,
  Modal,
  Pagination,
  Grid,
  Column,
  InlineLoading,
  InlineNotification,
  TextInput,
  TextArea,
} from "@carbon/react";
import { Add, Delete, Filter } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";
import { useNavigate } from "react-router-dom";

const headers = [
  { key: "planName", header: "Plan Name" },
  { key: "code", header: "Code" },
  { key: "planType", header: "Plan Type" },
  { key: "price", header: "Price" },
  { key: "gstPercentage", header: "GST (%)" },
  { key: "duration", header: "Duration" },
  { key: "trialDays", header: "Trial Days" },
  { key: "status", header: "Status" },
  { key: "createdAt", header: "Created At" },
];

const PAGE_SIZES = [5, 10, 20, 50];

export default function SubscriptionPlans() {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  // Form states
const [formData, setFormData] = useState({
  subscriptionName: "",
  code: "",
  planType: "",
  description: "",
  price: "",
  gstPercentage: "",
  currency: "INR",
  durationDays: 30,
  trialDays: 0,
  status: "active",
});


  // Form validation states
  const [validationErrors, setValidationErrors] = useState({});

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    currency: [],
    priceRange: { min: "", max: "" },
  });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Delete modal state
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters]);

  // Normalize subscription plan data (from backend to frontend)
 const normalizePlan = useCallback((item) => ({
  id: String(item.id),
  planId: item.id,

  planName: item.name || "-",
  code: item.code || "-",
  planType: item.plan_type || "",
  description: item.description || "",

  featuresJson: item.features || {
    title: "",
    features: [],
    footerTitle: "",
    footerDescription: "",
  },
  offers: item.offers || [],

  price: Number(item.price) || 0,
  gstPercentage: item.gstPercentage ?? 0,
  currency: item.currency || "INR",

  durationDays: item.durationDays || 0,
  trialDays: item.trialDays || 0,

  status: item.status || "inactive",

  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
  
}), []);

  // Fetch subscription plans
  const fetchPlans = useCallback(async () => {
    if (!mountedRef.current) return;
    
    setLoading(true);
    setError("");
    try {
      const response = await callApi("GET", "/subscription-plans/");
      
      const data = Array.isArray(response?.data) ? response.data : [];
      console.log(data);
      
      const formattedRows = data.map(item => normalizePlan(item));
      
      if (!mountedRef.current) return;
      setRows(formattedRows);
    } catch (err) {
      console.error("fetchPlans error:", err);
      if (!mountedRef.current) return;
      setError("Unable to load subscription plans. Please try again.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [normalizePlan]);

  // Initial data fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchPlans();
    
    return () => {
      mountedRef.current = false;
    };
  }, [fetchPlans]);

  // Apply filters
  const applyFilters = useCallback((data) => {
    if (!data || data.length === 0) return data;

    return data.filter(row => {
      // Filter by status
      if (filters.status.length > 0) {
        const hasMatchingStatus = filters.status.some(filter => 
          filter.id === row.status
        );
        if (!hasMatchingStatus) return false;
      }

      // Filter by currency
      if (filters.currency.length > 0) {
        const hasMatchingCurrency = filters.currency.some(filter => 
          filter.id === row.currency
        );
        if (!hasMatchingCurrency) return false;
      }

      // Filter by price range
      if (filters.priceRange.min && row.price < parseFloat(filters.priceRange.min)) {
        return false;
      }
      if (filters.priceRange.max && row.price > parseFloat(filters.priceRange.max)) {
        return false;
      }

      return true;
    });
  }, [filters]);

  // Filter rows based on search and filters
  const filteredRows = useMemo(() => {
    let filtered = rows;

    // Apply text search
    if (debouncedSearch) {
      filtered = filtered.filter(row =>
        (row.planName?.toLowerCase() || '').includes(debouncedSearch) ||
        (row.code?.toLowerCase() || '').includes(debouncedSearch) ||
        (row.description?.toLowerCase() || '').includes(debouncedSearch)
      );
    }

    // Apply filters
    filtered = applyFilters(filtered);

    // Sort by plan ID descending (newest first)
    return [...filtered].sort((a, b) => b.planId - a.planId);
  }, [rows, debouncedSearch, applyFilters]);

  const paginatedRows = useMemo(() =>
    filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize]
  );

  const handlePriceRangeChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      priceRange: {
        ...prev.priceRange,
        [type]: value
      }
    }));
    setPage(1);
  };

  const clearAllFilters = () => {
    setFilters({
      status: [],
      currency: [],
      priceRange: { min: "", max: "" },
    });
    setPage(1);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    count += filters.status.length;
    count += filters.currency.length;
    if (filters.priceRange.min) count++;
    if (filters.priceRange.max) count++;
    return count;
  }, [filters]);

  // Reset modal
  const resetModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setEditingRow(null);
    setFormData({
      subscriptionName: "",
      code: "",
      planType: "",
      description: "",
      price: "",
      gstPercentage: "",
      currency: "INR",
      durationDays: 30,
      trialDays: 0,
      status: "active",
featuresJson: JSON.stringify(
  {
    title: "",
    features: [
      { order: 1, title: "", description: "", icon: "" },
      { order: 2, title: "", description: "", icon: "" },
      { order: 3, title: "", description: "", icon: "" },
      { order: 4, title: "", description: "", icon: "" },
      { order: 5, title: "", description: "", icon: "" },
      { order: 6, title: "", description: "", icon: "" }
    ],
    footerTitle: "",
    footerDescription: ""
  },
  null,
  2
),
    });
    setValidationErrors({});
  };

  // Open edit modal
  const handleEdit = (row) => {
     
    setFormData({
      subscriptionName: row.planName,
      code: row.code,
      planType: row.planType || "",
      description: row.description || "",
      price: row.price.toString(),
      gstPercentage: row.gstPercentage,
      currency: row.currency,
      durationDays: row.durationDays,
      trialDays: row.trialDays,
      status: row.status,
      featuresJson: JSON.stringify(row.featuresJson, null, 2)
});
        setIsEdit(true);
    setEditingRow(row);
    setValidationErrors({});
    setIsModalOpen(true);
  };
  // Open Offers page
  const handleOffers = (row) => {

  navigate(`/settings/subscription/${row.id}/offers`, {
    state: {
      subscription: row,
    },
  });
};

  

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.subscriptionName.trim()) {
      errors.subscriptionName = "Plan Name is required";
    }
    
    if (!formData.code.trim()) {
      errors.code = "Plan Code is required";
    } else if (!/^[A-Z][A-Z0-9_]*$/.test(formData.code)) {
      errors.code = "Code must start with uppercase letter and contain only uppercase letters, numbers, and underscores";
    }
    
    if (!formData.price) {
      errors.price = "Price is required";
    } else if (isNaN(formData.price) || parseFloat(formData.price) < 0) {
      errors.price = "Price must be a valid positive number";
    }
    
    if (!formData.durationDays) {
      errors.durationDays = "Duration is required";
    } else if (formData.durationDays < 1) {
      errors.durationDays = "Duration must be at least 1 day";
    }
    
    if (formData.trialDays < 0) {
      errors.trialDays = "Trial days cannot be negative";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save subscription plan
  const handleSave = async (e) => {
    let parsedFeatures;

try {
  parsedFeatures = JSON.parse(formData.featuresJson);
} catch {
  setError("Features JSON is invalid.");
  return;
}
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      // Backend expects UPPERCASE field names
      const payload = {
        SUBSCRIPTION_NAME: formData.subscriptionName,
        CODE: formData.code,
        PLAN_TYPE: formData.planType,
        DESCRIPTION: formData.description,
        FEATURES_JSON: parsedFeatures,
        PRICE: parseFloat(formData.price),
        GST_PERCENTAGE: parseFloat(formData.gstPercentage),
        CURRENCY: formData.currency,

        DURATION_DAYS: parseInt(formData.durationDays),
        TRIAL_DAYS: parseInt(formData.trialDays),
        STATUS: formData.status,
      };

      if (isEdit && editingRow) {
        await callApi("PUT", `/subscription-plans/${editingRow.id}`, payload);
      } else {
        await callApi("POST", "/subscription-plans/", payload);
      }

      await fetchPlans();
      setSuccess(isEdit ? "Plan updated successfully!" : "Plan created successfully!");
      setTimeout(() => setSuccess(""), 3000);
      resetModal();
    } catch (err) {
      console.error("Save error:", err);
      
      // Handle validation errors from backend
      if (err.response?.data?.detail) {
        const backendErrors = err.response.data.detail;
        const formattedErrors = {};
        backendErrors.forEach(error => {
          const field = error.loc[1]?.toLowerCase();
          formattedErrors[field] = error.msg;
        });
        setValidationErrors(formattedErrors);
        setError("Please fix the validation errors.");
      } else {
        setError(err.message || "Failed to save plan. Please try again.");
      }
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Confirm delete
  const confirmDelete = (plan) => {
    setPlanToDelete(plan);
    setIsDeleteModalOpen(true);
  };

  // Handle single delete
  const handleConfirmDelete = async () => {
    if (!planToDelete) return;
    
    setDeleting(true);
    setError("");
    
    try {
      await callApi("DELETE", `/subscription-plans/${planToDelete.id}`);
      await fetchPlans();
      setSuccess("Plan deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
      setIsDeleteModalOpen(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error("Failed to delete plan:", error);
      setError("Failed to delete plan. Please try again.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setDeleting(false);
    }
  };

  // Handle batch delete
  const handleBatchDelete = async (selectedRows) => {
    if (selectedRows.length === 0) return;
    
    setDeleting(true);
    setError("");
    
    try {
      const deletePromises = selectedRows.map(row => 
        callApi("DELETE", `/subscription-plans/${row.id}`)
      );
      
      await Promise.all(deletePromises);
      await fetchPlans();
      setSuccess(`${selectedRows.length} plan(s) deleted successfully.`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Failed to delete plans:", error);
      setError("Failed to delete some plans. Please try again.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setDeleting(false);
    }
  };

  // Format price with currency
  const formatPrice = (price, currency) => {
    const currencySymbols = {
      INR: '₹',
      USD: '$',
      EUR: '€',
      GBP: '£'
    };
    const symbol = currencySymbols[currency] || currency;
    return `${symbol} ${price.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Required label helper
  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  return (
    <>
      <DataTable rows={paginatedRows} headers={headers} isSortable>
        {({ rows, headers, getHeaderProps, getRowProps, getSelectionProps, getBatchActionProps, selectedRows }) => {
          const batchActionProps = getBatchActionProps();
          
          return (
            <TableContainer title="Subscription Plans">
              <TableToolbar>
                {selectedRows.length > 0 ? (
                  <TableBatchActions {...batchActionProps}>
                    <TableBatchAction
                      renderIcon={Delete}
                      onClick={() => handleBatchDelete(selectedRows)}
                      iconDescription="Delete selected rows"
                      disabled={deleting}
                    >
                      Delete ({selectedRows.length})
                    </TableBatchAction>
                  </TableBatchActions>
                ) : (
                  <>
                    <TableToolbarContent>
                      <TableToolbarSearch
                        persistent
                        id="plan-search"
                        placeholder="Search plans..."
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />

                      <Button
                        kind="ghost"
                        renderIcon={Filter}
                        hasIconOnly
                        iconDescription="Toggle filters"
                        onClick={() => setShowFilters((v) => !v)}
                      >
                        {activeFilterCount > 0 && `(${activeFilterCount})`}
                      </Button>

                      <Button 
                        renderIcon={Add} 
                        onClick={() => {
                          resetModal();
                          setIsModalOpen(true);
                        }}
                      >
                        Add Plan
                      </Button>
                    </TableToolbarContent>
                  </>
                )}
              </TableToolbar>

              {/* Filter Section */}
              {showFilters && (
                <div
                  style={{
                    width: "100%",
                    padding: "1rem 1.25rem",
                    background: "#f4f4f4",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <Grid narrow>
                    <Column lg={4} md={4} sm={4}>
                      <TextInput
                        id="min-price"
                        labelText="Min Price"
                        placeholder="Minimum"
                        type="number"
                        value={filters.priceRange.min}
                        onChange={(e) => handlePriceRangeChange("min", e.target.value)}
                      />
                    </Column>
                    <Column lg={4} md={4} sm={4}>
                      <TextInput
                        id="max-price"
                        labelText="Max Price"
                        placeholder="Maximum"
                        type="number"
                        value={filters.priceRange.max}
                        onChange={(e) => handlePriceRangeChange("max", e.target.value)}
                      />
                    </Column>
                    <Column lg={4} md={4} sm={4} style={{ display: "flex", alignItems: "flex-end" }}>
                      <Button
                        kind="secondary"
                        size="sm"
                        onClick={clearAllFilters}
                        style={{ width: "100%" }}
                      >
                        Clear All
                      </Button>
                    </Column>
                  </Grid>
                </div>
              )}

              {/* Loading and Error Messages */}
              {loading && (
                <div style={{ padding: "1rem", textAlign: "center" }}>
                  <InlineLoading description="Loading subscription plans..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              {success && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="success"
                    title="Success"
                    subtitle={success}
                    lowContrast
                    onClose={() => setSuccess("")}
                  />
                </div>
              )}

              <Table>
                <TableHead>
                  <TableRow>
                    <th />
                    {headers.map((header) => {
                      const { key, ...headerProps } = getHeaderProps({ header });

                      return (
                        <TableHeader key={key} {...headerProps}>
                          {header.header}
                        </TableHeader>
                      );
                    })}
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length + 2} style={{ textAlign: 'center', padding: '2rem' }}>
                        {searchTerm || activeFilterCount > 0 
                          ? 'No results found. Try adjusting your search or filters.' 
                          : 'No subscription plans found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                paginatedRows.map((row) => {
                  const { key: rowKey, ...rowProps } = getRowProps({ row });
                  const { key: selectKey, ...selectProps } = getSelectionProps({ row });

                  return (
                    <TableRow key={rowKey} {...rowProps}>
                      <TableSelectRow key={selectKey} {...selectProps} />
                        <TableCell>
                          <div>
                            <strong>{row.planName}</strong>
                            {row.description && (
                              <div style={{ fontSize: '12px', color: '#6f6f6f' }}>
                                {row.description.substring(0, 50)}
                                {row.description.length > 50 && '...'}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <code style={{ fontSize: '12px' }}>{row.code}</code>
                        </TableCell>
                        <TableCell>
  {row.planType || "-"}
</TableCell>
                        <TableCell>
                          <strong>{formatPrice(row.price, row.currency)}</strong>
                        </TableCell>
                        <TableCell>
  {row.gstPercentage != null ? `${row.gstPercentage}%` : "-"}
</TableCell>
                        <TableCell>{row.durationDays} days</TableCell>
                        <TableCell>{row.trialDays} days</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{formatDate(row.createdAt)}</TableCell>
                        <TableCell>
                          <TableToolbarMenu>
                            <TableToolbarAction onClick={() => handleOffers(row)}>
                              Offers
                            </TableToolbarAction>

                          
                            <TableToolbarAction onClick={() => handleEdit(row)}>
                              Edit
                            </TableToolbarAction>

                            <TableToolbarAction onClick={() => confirmDelete(row)}>
                              Delete
                            </TableToolbarAction>
                          </TableToolbarMenu>
                        </TableCell>
                            </TableRow>
                          );
                        })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* Pagination */}
      {filteredRows.length > pageSize && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={filteredRows.length}
            page={page}
            pageSize={pageSize}
            pageSizes={PAGE_SIZES}
            onChange={({ page: p, pageSize: ps }) => { 
              setPage(p); 
              setPageSize(ps); 
            }}
          />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        modalHeading={isEdit ? "Edit Subscription Plan" : "Add Subscription Plan"}
        primaryButtonText={loading ? <InlineLoading description="Saving..." /> : (isEdit ? "Update" : "Create")}
        secondaryButtonText="Cancel"
        onRequestClose={resetModal}
        onRequestSubmit={handleSave}
        size="lg"
        primaryButtonDisabled={loading}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '8px' }}>
          <Grid>
            <Column lg={12}>
              <div style={{ marginBottom: "1rem" }}>
                <TextInput
                  id="subscriptionName"
                  labelText={requiredLabel("Plan Name")}
                  value={formData.subscriptionName}
                  onChange={(e) => handleInputChange("subscriptionName", e.target.value)}
                  placeholder="Enter plan name"
                  invalid={!!validationErrors.subscriptionName}
                  invalidText={validationErrors.subscriptionName}
                  required
                />
              </div>
            </Column>
            <Column lg={6}>
              <div style={{ marginBottom: "1rem" }}>
                <TextInput
                  id="code"
                  labelText={requiredLabel("Plan Code")}
                  value={formData.code}
                  onChange={(e) => handleInputChange("code", e.target.value.toUpperCase())}
                  placeholder="Enter plan code (e.g., BASIC_MONTHLY)"
                  invalid={!!validationErrors.code}
                  invalidText={validationErrors.code}
                  required
                />
              </div>
            </Column>
            <Column lg={6}>
              <div style={{ marginBottom: "1rem" }}>
                <TextInput
                  id="planType"
                  labelText={requiredLabel("Plan Type")}
                  value={formData.planType}
                  onChange={(e) =>handleInputChange("planType", e.target.value)}
                  placeholder="Enter plan type"
                  invalid={!!validationErrors.planType}
                  invalidText={validationErrors.planType}
                  required
                />
              </div>
            </Column>
            <Column lg={6}>
              <div style={{ marginBottom: "1rem" }}>
                <TextInput
                  id="description"
                  labelText="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  placeholder="Enter plan description"
                />
              </div>
            </Column>
            <Column lg={12}>
              <div style={{ marginBottom: "1rem" }}>
              <TextArea
      id="featuresJson"
      labelText="Features JSON"
      rows={12}
      value={formData.featuresJson}
      onChange={(e) =>
        handleInputChange("featuresJson", e.target.value)
      }
      helperText="Enter valid JSON"
    />
  </div>
</Column>
            <Column lg={6}>
              <div style={{ marginBottom: "1rem" }}>
                <TextInput
                  id="price"
                  labelText={requiredLabel("Price")}
                  value={formData.price}
                  onChange={(e) => handleInputChange("price", e.target.value)}
                  placeholder="0.00"
                  type="number"
                  step="0.01"
                  invalid={!!validationErrors.price}
                  invalidText={validationErrors.price}
                  required
                />
              </div>
            </Column>
            <Column lg={6}>
  <div style={{ marginBottom: "1rem" }}>
    <TextInput
      id="gstPercentage"
      labelText="GST Percentage"
      value={formData.gstPercentage}
      onChange={(e) => handleInputChange("gstPercentage", e.target.value)}
      placeholder="18"
      type="number"
      step="0.01"
    />
  </div>
</Column>
            <Column lg={6}>
              <div style={{ marginBottom: "1rem" }}>
                <TextInput
                  id="currency"
                  labelText="Currency"
                  value={formData.currency}
                  onChange={(e) => handleInputChange("currency", e.target.value.toUpperCase())}
                  placeholder="INR"
                  invalid={!!validationErrors.currency}
                  invalidText={validationErrors.currency}
                  helperText="Enter currency code (INR, USD, EUR, GBP)"
                  required
                  disabled={isEdit} 
                />
              </div>
            </Column>
            <Column lg={6}>
              <div style={{ marginBottom: "1rem" }}>
                <TextInput
                  id="durationDays"
                  labelText={requiredLabel("Duration (Days)")}
                  value={formData.durationDays}
                  onChange={(e) => handleInputChange("durationDays", e.target.value)}
                  type="number"
                  step="1"
                  invalid={!!validationErrors.durationDays}
                  invalidText={validationErrors.durationDays}
                  required
                />
              </div>
            </Column>
            <Column lg={6}>
              <div style={{ marginBottom: "1rem" }}>
                <TextInput
                  id="trialDays"
                  labelText="Trial Days"
                  value={formData.trialDays}
                  onChange={(e) => handleInputChange("trialDays", e.target.value)}
                  placeholder="0"
                  type="number"
                  step="1"
                  invalid={!!validationErrors.trialDays}
                  invalidText={validationErrors.trialDays}
                />
              </div>
            </Column>
          </Grid>
        </div>
      </Modal>
      

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText={deleting ? <InlineLoading description="Deleting..." /> : "Delete"}
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => {
          if (!deleting) {
            setIsDeleteModalOpen(false);
            setPlanToDelete(null);
          }
        }}
        onRequestSubmit={handleConfirmDelete}
        primaryButtonDisabled={deleting}
      >
        <p>Are you sure you want to delete the plan "{planToDelete?.planName}"? This action cannot be undone.</p>
      </Modal>
    </>
  );
}